const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Thin fetch wrapper: always sends the session cookie, never caches, throws ApiError on failure. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      // Only declare a JSON body when we're actually sending one — Fastify's
      // body parser rejects an empty body with Content-Type: application/json
      // (a bodyless POST like /api/auth/logout used to fail with 400
      // FST_ERR_CTP_EMPTY_JSON_BODY, which silently broke the logout button:
      // the request threw before the client ever cleared its user state).
      ...(init?.body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "unknown", message: response.statusText }));
    throw new ApiError(response.status, body.error ?? "unknown", body.message ?? response.statusText);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const SEAT_STATUSES = ["available", "blocked", "reserved", "technical", "emergency"] as const;
export type SeatStatus = (typeof SEAT_STATUSES)[number];

export interface CurrentUser {
  id: number;
  email: string;
  displayName: string;
  locale: string;
  themePreference: string;
  permissions: string[];
}

export const api = {
  login: (email: string, password: string, rememberMe: boolean) =>
    request<{ user: CurrentUser }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password, rememberMe }) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  me: () => request<{ user: CurrentUser | null }>("/api/auth/me"),
  eventSessions: () => request<Array<{ id: number; name: string; event: { name: string }; layout: { name: string; venue: { name: string } } }>>("/api/event-sessions"),
  sessionSeating: (eventSessionId: number) =>
    request<{
      seats: Array<{
        id: number;
        block: string;
        row: string;
        seatNumber: number;
        label: string | null;
        positionX: number;
        positionY: number;
        rotation: number;
        status: string;
        tooltipLabel: string | null;
        occupant: {
          participantsId: number;
          vorname: string | null;
          nachname: string | null;
          region: string | null;
          bezirk: string | null;
          zielgruppe: string | null;
          registrationStatus: string | null;
        } | null;
        warnings: string[];
      }>;
      warnings: Array<{ type: string; seatKey: string; participantsId: number | null; message: string }>;
    }>(`/api/event-sessions/${eventSessionId}/seating`),
  async exportPdf(params: { eventSessionId: number; colorMode: string; paperSize: string; locale: string }): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/api/export/pdf`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: "unknown", message: response.statusText }));
      throw new ApiError(response.status, body.error ?? "unknown", body.message ?? response.statusText);
    }
    return response.blob();
  },
  bulkUpdateSeatStatus: (ids: number[], status: SeatStatus, note?: string) => request<{ updated: number }>("/api/seats/bulk", { method: "PATCH", body: JSON.stringify({ ids, status, note }) }),

  getDbConnection: () =>
    request<{
      connection: {
        id: number;
        host: string;
        port: number;
        username: string;
        database: string;
        lastTestedAt: string | null;
        lastTestOk: boolean | null;
        lastTestMessage: string | null;
      } | null;
    }>("/api/settings/db-connection"),
  saveDbConnection: (input: { host: string; port: number; username: string; password: string; database: string }) =>
    request<{ connection: unknown }>("/api/settings/db-connection", { method: "PUT", body: JSON.stringify(input) }),
  testDbConnection: () => request<{ ok: boolean; message: string }>("/api/settings/db-connection/test", { method: "POST" }),
  getSeatplanViews: () => request<{ views: string[] }>("/api/settings/db-connection/views"),

  async createProject(params: { name: string; participantSourceKey: string; geometryCsv: File }): Promise<{ eventSessionId: number; seatCount: number; warnings: string[] }> {
    const form = new FormData();
    form.set("name", params.name);
    form.set("participantSourceKey", params.participantSourceKey);
    form.set("geometryCsv", params.geometryCsv);
    const response = await fetch(`${API_BASE_URL}/api/projects`, { method: "POST", credentials: "include", body: form });
    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: "unknown", message: response.statusText }));
      throw new ApiError(response.status, body.error ?? "unknown", body.message ?? response.statusText);
    }
    return response.json();
  },
};
